/**
 * Solar geometry calculations for the Netherlands.
 *
 * Contains NOAA-based sunrise/sunset calculation, solar position functions,
 * and orientation/tilt estimation from production curves.
 *
 * @module solar
 */

import { NL_LATITUDE, NL_LONGITUDE } from './constants'

const RAD = Math.PI / 180
const DEG = 180 / Math.PI

// ─── Sunrise / Sunset (NOAA algorithm) ──────────────────────────────────────

/**
 * Calculate sunrise and sunset times for a given date at NL coordinates.
 * @param {Date} date
 * @param {number} lat - Latitude (default: NL)
 * @param {number} lon - Longitude (default: NL)
 * @returns {{ sunrise: number, sunset: number } | null} Minutes from midnight (local time)
 */
export function getSunriseSunset(date, lat = NL_LATITUDE, lon = NL_LONGITUDE) {
  const toRad = d => d * RAD
  const toDeg = r => r * DEG

  const utcNoon = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0)
  const JD = utcNoon / 86400000 + 2440587.5
  const T = (JD - 2451545.0) / 36525.0

  const L0 = (280.46646 + T * (36000.76983 + T * 0.0003032)) % 360
  const M = 357.52911 + T * (35999.05029 - 0.0001537 * T)
  const e = 0.016708634 - T * (0.000042037 + 0.0000001267 * T)
  const Mrad = toRad(M)

  const C =
    Math.sin(Mrad) * (1.914602 - T * (0.004817 + 0.000014 * T)) +
    Math.sin(2 * Mrad) * (0.019993 - 0.000101 * T) +
    Math.sin(3 * Mrad) * 0.000289

  const omega = 125.04 - 1934.136 * T
  const lambda = L0 + C - 0.00569 - 0.00478 * Math.sin(toRad(omega))

  const epsilon0 =
    23 + (26 + (21.448 - T * (46.815 + T * (0.00059 - T * 0.001813))) / 60) / 60
  const epsilon = epsilon0 + 0.00256 * Math.cos(toRad(omega))

  const decl = toDeg(Math.asin(Math.sin(toRad(epsilon)) * Math.sin(toRad(lambda))))
  const y = Math.tan(toRad(epsilon / 2)) ** 2
  const L0rad = toRad(L0)

  const EqT =
    4 *
    toDeg(
      y * Math.sin(2 * L0rad) -
        2 * e * Math.sin(Mrad) +
        4 * e * y * Math.sin(Mrad) * Math.cos(2 * L0rad) -
        0.5 * y * y * Math.sin(4 * L0rad) -
        1.25 * e * e * Math.sin(2 * Mrad)
    )

  const latRad = toRad(lat)
  const declRad = toRad(decl)
  const cosHArg =
    Math.cos(toRad(90.833)) / (Math.cos(latRad) * Math.cos(declRad)) -
    Math.tan(latRad) * Math.tan(declRad)

  if (Math.abs(cosHArg) > 1) return null // polar day/night

  const HA = toDeg(Math.acos(cosHArg))
  const solarNoon = 720 - 4 * lon - EqT

  const offsetMin = getNlUtcOffsetMinutes(date)

  return {
    sunrise: Math.round(solarNoon - 4 * HA + offsetMin),
    sunset: Math.round(solarNoon + 4 * HA + offsetMin),
  }
}

/**
 * Find the label index nearest to a target time in minutes.
 * @param {string[]} labels - Array of 'HH:mm' strings
 * @param {number} targetMinutes - Minutes from midnight
 * @param {number} maxDiffMinutes - Max allowed difference
 * @returns {number} Index or -1 if no match within threshold
 */
export function findNearestLabelIndex(labels, targetMinutes, maxDiffMinutes = 15) {
  const toMins = t => {
    const [h, m] = t.split(':').map(Number)
    return h * 60 + m
  }
  let bestIdx = -1
  let bestDiff = Infinity
  labels.forEach((label, idx) => {
    const diff = Math.abs(toMins(label) - targetMinutes)
    if (diff < bestDiff) { bestDiff = diff; bestIdx = idx }
  })
  return bestDiff <= maxDiffMinutes ? bestIdx : -1
}

// ─── Solar position helpers ─────────────────────────────────────────────────

/**
 * Get Netherlands UTC offset in minutes (handles CET/CEST DST).
 */
export function getNlUtcOffsetMinutes(date) {
  const year = date.getFullYear()
  const lastSunMar = 31 - new Date(year, 2, 31).getDay()
  const lastSunOct = 31 - new Date(year, 9, 31).getDay()
  const dstStart = new Date(year, 2, lastSunMar, 2, 0)
  const dstEnd = new Date(year, 9, lastSunOct, 3, 0)
  const isDST = date >= dstStart && date < dstEnd
  return isDST ? 120 : 60
}

/**
 * Compute solar noon in minutes from midnight (local NL time).
 */
export function solarNoonMinutes(date) {
  const year = date.getFullYear()
  const start = new Date(year, 0, 0)
  const dayOfYear = Math.floor((date - start) / 86400000)

  // Equation of time (Spencer 1971 approximation)
  const B = (2 * Math.PI / 365) * (dayOfYear - 81)
  const eot = 9.87 * Math.sin(2 * B) - 7.53 * Math.cos(B) - 1.5 * Math.sin(B)

  // Longitude correction: 5°E vs CET meridian 15°E
  const longCorrection = (NL_LONGITUDE - 15.0) * 4 // -40 minutes

  const utcOffset = getNlUtcOffsetMinutes(date)

  return 720 - eot + longCorrection + utcOffset
}

/**
 * Compute solar declination for a date (degrees).
 */
export function solarDeclination(date) {
  const start = new Date(date.getFullYear(), 0, 0)
  const dayOfYear = Math.floor((date - start) / 86400000)
  return 23.45 * Math.sin(RAD * (360 / 365) * (dayOfYear - 81))
}

/**
 * Compute solar elevation angle at a given time.
 * @param {Date} date
 * @param {number} minutesFromMidnight
 * @returns {number} Elevation in degrees
 */
export function solarElevation(date, minutesFromMidnight) {
  const decl = solarDeclination(date) * RAD
  const lat = NL_LATITUDE * RAD
  const noon = solarNoonMinutes(date)
  const hourAngle = ((minutesFromMidnight - noon) / 60) * 15 * RAD

  return Math.asin(
    Math.sin(lat) * Math.sin(decl) +
    Math.cos(lat) * Math.cos(decl) * Math.cos(hourAngle)
  ) * DEG
}

/**
 * Compute solar azimuth at a given time.
 * @param {Date} date
 * @param {number} minutesFromMidnight
 * @returns {number} Azimuth in degrees (180 = south)
 */
export function solarAzimuth(date, minutesFromMidnight) {
  const decl = solarDeclination(date) * RAD
  const lat = NL_LATITUDE * RAD
  const noon = solarNoonMinutes(date)
  const hourAngle = ((minutesFromMidnight - noon) / 60) * 15 * RAD

  const elevation = Math.asin(
    Math.sin(lat) * Math.sin(decl) +
    Math.cos(lat) * Math.cos(decl) * Math.cos(hourAngle)
  )

  const cosAz = (Math.sin(decl) - Math.sin(lat) * Math.sin(elevation)) /
    (Math.cos(lat) * Math.cos(elevation))

  let azimuth = Math.acos(Math.max(-1, Math.min(1, cosAz))) * DEG
  if (minutesFromMidnight > noon) azimuth = 360 - azimuth

  return azimuth
}

// ─── Production curve analysis ──────────────────────────────────────────────

/**
 * Assess how "clear" a day's production curve is (0-1, higher = smoother/clearer).
 */
export function daySmoothnessScore(quarterHourData) {
  const sunData = quarterHourData
    .filter(item => {
      const h = parseInt(item.date?.substring(11, 13) || '0')
      return h >= 6 && h <= 20
    })
    .map(item => Math.max(0, item.delta_power ?? 0))

  if (sunData.length < 20) return 0

  const peak = Math.max(...sunData)
  if (peak === 0) return 0

  // Only score the productive core (above 20% of peak) to ignore dawn/dusk noise
  const threshold = peak * 0.2
  const core = []
  for (let i = 0; i < sunData.length; i++) {
    if (sunData[i] >= threshold) core.push(sunData[i])
  }

  if (core.length < 10) return 0

  // Count direction reversals: a smooth curve has few reversals,
  // a cloudy day oscillates constantly
  let reversals = 0
  for (let i = 2; i < core.length; i++) {
    const prev = core[i - 1] - core[i - 2]
    const curr = core[i] - core[i - 1]
    // A reversal is when the direction changes (positive to negative or vice versa)
    // and the magnitude is meaningful (not just ±1 noise)
    if (prev * curr < 0 && (Math.abs(prev) + Math.abs(curr)) > peak * 0.03) {
      reversals++
    }
  }

  // Normalize: 0 reversals = 1.0, many reversals = 0.0
  // A clear day typically has 0-2 reversals, cloudy can have 10+
  const maxReversals = core.length * 0.4
  return Math.max(0, Math.min(1, 1 - (reversals / maxReversals)))
}

/**
 * Estimate panel orientation (azimuth) from production curve peak timing.
 * @returns {number|null} Azimuth in degrees (180 = south)
 */
export function estimateOrientation(quarterHourData, date) {
  const sunData = quarterHourData
    .filter(item => {
      const h = parseInt(item.date?.substring(11, 13) || '0')
      return h >= 7 && h <= 19
    })
    .map(item => ({
      minutes: parseInt(item.date?.substring(11, 13) || '0') * 60 +
               parseInt(item.date?.substring(14, 16) || '0'),
      power: Math.max(0, item.delta_power ?? 0),
    }))
    .filter(d => d.power > 0)

  if (sunData.length < 10) return null

  // Production-weighted center of mass (top 50%)
  let weightedSum = 0
  let totalWeight = 0
  const maxPower = Math.max(...sunData.map(d => d.power))
  const threshold = maxPower * 0.5

  sunData.forEach(d => {
    if (d.power >= threshold) {
      weightedSum += d.minutes * d.power
      totalWeight += d.power
    }
  })

  if (totalWeight === 0) return null

  const peakCenterMinutes = weightedSum / totalWeight
  const noon = solarNoonMinutes(date)
  const offsetMinutes = peakCenterMinutes - noon
  const azimuthOffset = (offsetMinutes / 60) * 15

  return Math.round(Math.max(60, Math.min(300, 180 + azimuthOffset)))
}

/**
 * Estimate panel tilt from peak production vs theoretical maximum.
 * @returns {number|null} Tilt in degrees
 */
export function estimateTilt(quarterHourData, date, wpKwp) {
  if (!wpKwp || wpKwp <= 0) return null

  const sunData = quarterHourData
    .filter(item => {
      const h = parseInt(item.date?.substring(11, 13) || '0')
      return h >= 8 && h <= 18
    })
    .map(item => ({
      minutes: parseInt(item.date?.substring(11, 13) || '0') * 60 +
               parseInt(item.date?.substring(14, 16) || '0'),
      power: Math.max(0, item.delta_power ?? 0),
    }))

  if (sunData.length < 10) return null

  let peakPower = 0
  let peakMinutes = 720
  sunData.forEach(d => {
    if (d.power > peakPower) { peakPower = d.power; peakMinutes = d.minutes }
  })

  const maxElev = solarElevation(date, peakMinutes)
  if (maxElev <= 10) return null

  const theoreticalHorizontal = wpKwp * 1000 * Math.sin(maxElev * RAD) * 0.25
  if (theoreticalHorizontal <= 0) return null

  const ratio = peakPower / theoreticalHorizontal
  if (ratio < 0.5) return null

  return Math.round(Math.max(0, Math.min(60,
    (90 - maxElev) * Math.min(ratio, 1.5)
  )))
}

/**
 * Convert azimuth degrees to Dutch compass label.
 */
export function azimuthToLabel(az) {
  if (az >= 350 || az < 10) return 'Noord'
  if (az < 35) return 'Noord-Noordoost'
  if (az < 55) return 'Noordoost'
  if (az < 80) return 'Oost-Noordoost'
  if (az < 100) return 'Oost'
  if (az < 125) return 'Oost-Zuidoost'
  if (az < 145) return 'Zuidoost'
  if (az < 170) return 'Zuid-Zuidoost'
  if (az < 190) return 'Zuid'
  if (az < 215) return 'Zuid-Zuidwest'
  if (az < 235) return 'Zuidwest'
  if (az < 260) return 'West-Zuidwest'
  if (az < 280) return 'West'
  if (az < 305) return 'West-Noordwest'
  if (az < 325) return 'Noordwest'
  return 'Noord-Noordwest'
}

/**
 * Find the clearest day and estimate orientation/tilt.
 */
export function estimateOrientationAndTilt(mainData, dateList, mainWp) {
  if (!mainData || mainData.length === 0) return null

  const mainKwp = mainWp ? mainWp * 0.001 : null

  const byDate = {}
  mainData.forEach(item => {
    const d = item.date?.substring(0, 10)
    if (d) { (byDate[d] = byDate[d] || []).push(item) }
  })

  let bestDay = null
  let bestScore = 0
  let bestDate = null

  for (const dateStr of dateList) {
    const dayData = byDate[dateStr]
    if (!dayData || dayData.length < 20) continue

    const score = daySmoothnessScore(dayData)
    if (score > bestScore) {
      bestScore = score
      bestDay = dayData
      bestDate = dateStr
    }
  }

  if (!bestDay || bestScore < 0.4 || !bestDate) return null

  const date = new Date(bestDate)
  const azimuth = estimateOrientation(bestDay, date)
  const tilt = estimateTilt(bestDay, date, mainKwp)

  if (azimuth === null) return null

  return {
    azimuth,
    tilt,
    clearDay: bestDate,
    smoothness: Math.round(bestScore * 100),
    azimuthLabel: azimuthToLabel(azimuth),
  }
}
